import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { YStack, isWeb } from 'tamagui';

import { AppButton, AppCard, AppText } from './primitives';

type NetworkStatus = 'online' | 'offline' | 'unstable';

type NetworkStatusContextValue = {
  status: NetworkStatus;
  message: string | null;
  isOffline: boolean;
  hasConnectionIssue: boolean;
  reportConnectionIssue: (error?: unknown, fallbackMessage?: string) => void;
  clearConnectionIssue: () => void;
  markConnectionHealthy: () => void;
};

const DEFAULT_OFFLINE_MESSAGE =
  'Adulting cannot reach the internet right now. What is already on screen is still here. Try again when your connection settles.';
const DEFAULT_UNSTABLE_MESSAGE =
  'Adulting could not refresh everything just now because the connection looks shaky. What is already on screen is still here.';

const NetworkStatusContext = createContext<NetworkStatusContextValue | null>(null);

function getErrorMessage(error: unknown) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error && 'message' in error) {
    return String((error as { message?: unknown }).message || '');
  }
  return String(error);
}

export function isTransientConnectionError(error: unknown) {
  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('aborterror') ||
    message.includes('network request failed') ||
    message.includes('internet connection appears to be offline')
  );
}

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const [browserOffline, setBrowserOffline] = useState(() => {
    if (!isWeb || typeof navigator === 'undefined') {
      return false;
    }
    return navigator.onLine === false;
  });
  const [connectionIssueMessage, setConnectionIssueMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => {
      setBrowserOffline(false);
      setConnectionIssueMessage(null);
    };

    const handleOffline = () => {
      setBrowserOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const reportConnectionIssue = useCallback((error?: unknown, fallbackMessage = DEFAULT_UNSTABLE_MESSAGE) => {
    if (error && !isTransientConnectionError(error)) {
      return;
    }

    setConnectionIssueMessage(browserOffline ? DEFAULT_OFFLINE_MESSAGE : fallbackMessage);
  }, [browserOffline]);

  const clearConnectionIssue = useCallback(() => {
    setConnectionIssueMessage(null);
  }, []);

  const markConnectionHealthy = useCallback(() => {
    if (!browserOffline) {
      setConnectionIssueMessage(null);
    }
  }, [browserOffline]);

  const value = useMemo<NetworkStatusContextValue>(() => {
    const status: NetworkStatus = browserOffline
      ? 'offline'
      : connectionIssueMessage
        ? 'unstable'
        : 'online';

    return {
      status,
      message: browserOffline ? DEFAULT_OFFLINE_MESSAGE : connectionIssueMessage,
      isOffline: browserOffline,
      hasConnectionIssue: browserOffline || Boolean(connectionIssueMessage),
      reportConnectionIssue,
      clearConnectionIssue,
      markConnectionHealthy,
    };
  }, [browserOffline, clearConnectionIssue, connectionIssueMessage, markConnectionHealthy, reportConnectionIssue]);

  return <NetworkStatusContext.Provider value={value}>{children}</NetworkStatusContext.Provider>;
}

export function useNetworkStatus() {
  const value = useContext(NetworkStatusContext);
  if (!value) {
    throw new Error('useNetworkStatus must be used within NetworkStatusProvider');
  }
  return value;
}

export function AppConnectionBanner({
  onRetry,
  retryLabel = 'Try again',
}: {
  onRetry?: (() => void) | null;
  retryLabel?: string;
}) {
  const { hasConnectionIssue, isOffline, message } = useNetworkStatus();

  if (!hasConnectionIssue) {
    return null;
  }

  return (
    <AppCard backgroundColor="$accentTint" borderColor="$accent" gap="$sm">
      <AppText fontSize="$sm" fontWeight="700">
        {isOffline ? "You're offline" : 'Connection looks shaky'}
      </AppText>
      <YStack gap="$sm">
        <AppText fontSize="$sm">
          {message || (isOffline ? DEFAULT_OFFLINE_MESSAGE : DEFAULT_UNSTABLE_MESSAGE)}
        </AppText>
        {onRetry ? (
          <AppButton size="sm" tone="outline" alignSelf="flex-start" onPress={onRetry}>
            {retryLabel}
          </AppButton>
        ) : null}
      </YStack>
    </AppCard>
  );
}
